import { SxProps } from '@mui/material';
import { lightTheme } from '../../theme/index';

const { palette, spacing } = lightTheme;

const baseStyles = {
  maxWidth: '100%',
  display: {
    md: 'flex'
  },
  flexWrap: 'nowrap',
  flexDirection: {
    xs: 'column',
    md: 'row-reverse'
  },
  gap: { sm: spacing(1), md: spacing(4) },
  padding: {
    xs: `${spacing(2)} ${spacing(1)} ${spacing(1)} ${spacing(1)}`,
    md: `${spacing(2)} ${spacing(4)}`
  }
};

const containerText = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: {
    xs: 'flex-start',
    md: 'center'
  },
  minHeight: '100%',
  pr: 0,
  '& .contentBox > *:last-child': {
    marginBottom: 0
  }
};

const textTitle = {
  overflowWrap: 'break-word',
  color: palette.primary.main,
  width: '100%',
  marginBottom: spacing(1)
};

const subtitleText = {
  color: palette.grey.main,
  width: {
    xs: '100%',
    md: '98%',
    lg: '80%'
  },
  marginBottom: spacing(1),
  textTransform: 'none',
  whiteSpace: 'pre-wrap'
};

const descriptionText = {
  display: 'inline-block',
  textTransform: 'none',
  color: palette.grey.main,
  width: {
    xs: '100%',
    md: '90%'
  },
  marginTop: '0.5rem',
  marginBottom: {
    xs: '1.5rem',
    md: '1rem'
  }
};

const bulletText = {
  color: palette.grey.main
};

const styles: Record<string, SxProps> = {
  contentBox: {
    width: '100%'
  },
  // Main Container Styles
  base: {
    ...baseStyles
  },
  normalLeft: {
    ...baseStyles
  },
  normalLeftExtreme: {
    ...baseStyles
  },
  reverseLeft: {
    ...baseStyles,
    flexDirection: {
      xs: 'column',
      md: 'row'
    }
  },
  reverseCenter: {
    ...baseStyles,
    flexDirection: {
      xs: 'column',
      md: 'row'
    }
  },
  normalCenterAqua: {
    ...baseStyles,
    backgroundColor: palette.primary['50']
  },
  reverseCenterAqua: {
    ...baseStyles,
    flexDirection: {
      xs: 'column',
      md: 'row'
    },
    backgroundColor: palette.primary['50']
  },
  normalCenterGreen: {
    ...baseStyles,
    flexDirection: {
      xs: 'column-reverse',
      md: 'row'
    },
    backgroundColor: palette.primary.main
  },
  alexanderHall: {
    ...baseStyles,
    flexDirection: {
      xs: 'column-reverse',
      md: 'row-reverse'
    },
    backgroundColor: palette.primary['400']
  },
  redGradient: {
    ...baseStyles,
    flexDirection: {
      xs: 'column-reverse',
      md: 'row-reverse'
    },
    background: palette.gradient['500'],
    backgroundClip: 'content-box'
  },
  vimeoContainer: {
    width: '100%',
    height: '500px',
    display: 'flex',
    justifyContent: 'center'
  },

  // alexanderHallBox
  alexanderHallBox: {
    backgroundColor: palette.primary['400']
  },

  // Text Container Layout
  textContainer: {
    ...containerText,
    alignItems: 'flex-end',
    '.hyper-link': {
      fontWeight: {
        xs: '400',
        md: 'bold'
      }
    }
  },
  textContainerVariant: {
    ...containerText,
    alignItems: 'flex-start'
  },
  textContainerRight: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: {
      xs: 'flex-start',
      md: 'center'
    },
    minHeight: {
      xs: '0',
      md: '68vh',
      lg: '77vh'
    },
    paddingLeft: {
      xs: '0',
      md: '3rem'
    }
  },

  // Title Text (Heading: h1, h2)
  title: {
    ...textTitle
  },
  titleVariant5: {
    ...textTitle,
    color: palette.grey['50']
  },
  titleVariantBlack: {
    ...textTitle,
    color: palette.grey.main
  },

  // Subtitle text (Heading: h3...)
  subtitle: {
    ...subtitleText
  },
  subtitleVariant5: {
    ...subtitleText,
    color: palette.grey['50']
  },

  // Description text (body)
  description: {
    ...descriptionText
  },
  descriptionVariant5: {
    ...descriptionText,
    color: palette.grey['50']
  },

  greenHeading: {
    textTransform: 'none',
    color: palette.primary.main,
    width: {
      xs: '100%',
      md: '90%'
    },
    marginTop: '0.5rem',
    marginBottom: {
      xs: '1.5rem',
      md: '1rem'
    }
  },

  // Italic text
  italicText: {
    color: palette.grey.main,
    marginBottom: spacing(1),
    whiteSpace: 'pre-wrap',

    a: {
      color: palette.primary.main,
      textDecoration: 'underline'
    }
  },

  anchorText: {
    color: palette.primary.main,
    textDecoration: 'underline'
  },

  bulletPointsContainer: {
    width: '100%',
    height: '100%',
    marginTop: {
      md: '1rem',
      lg: '0'
    },
    marginBottom: {
      xs: '2rem',
      md: '1rem',
      lg: '1.5rem'
    }
  },
  bulletPointBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.875rem',
    paddingY: '1.0rem'
  },
  iconTickStyle: {
    fontSize: '1.75rem'
  },

  btnLink: {
    textDecoration: 'none'
  },
  bulletTextStyle: {
    ...bulletText
  },
  bulletTextVariant5: {
    ...bulletText,
    color: palette.grey['50']
  },
  btnSecondary: {
    width: {
      xs: '100%',
      md: 'auto'
    },
    textTransform: 'none',
    textAlign: 'center',
    '& span > svg': {
      fontSize: '0.95rem !important' // Note: !important used to override MUI Default
    }
  },
  btnBox: {
    display: 'flex',
    flexDirection: {
      xs: 'column',
      md: 'row'
    },
    width: '100%',
    alignItems: 'center',
    gap: spacing(0.5),
    marginY: spacing(2),
    '& > div': {
      width: { xs: '100%', md: 'auto' }
    }
  },

  // Image container
  bkImgBase: {
    width: '100%',
    height: '100%',
    maxHeight: '40rem',
    display: {
      md: 'flex',
      xs: 'block'
    },
    marginBottom: {
      xs: spacing(1),
      lg: 'auto'
    },
    '& img': {
      objectFit: 'cover',
      marginLeft: {
        xs: 'none',
        lg: 'auto'
      },

      width: {
        xs: '100%',
        ml: '95%',
        xl: '80%'
      },
      maxHeight: '40rem'
    }
  },
  bkImgNormalLeft: {
    width: {
      xs: `calc(100% + ${spacing(2)})`,
      md: '100%'
    },
    height: '100%',
    maxHeight: '40rem',
    display: {
      md: 'flex',
      xs: 'block'
    },
    mx: {
      xs: `-${spacing(1)}`,
      md: '0'
    },
    mb: { xs: spacing(2), md: '0' },
    '& img': {
      objectFit: 'cover',
      marginLeft: {
        xs: 'none',
        lg: 'auto'
      },
      width: {
        xs: '100%',
        ml: '70%',
        xl: '65%'
      },
      maxHeight: '40rem'
    }
  },
  bkImgNormalLeftExtreme: {
    width: '100%',
    height: '100%',
    maxHeight: '40rem',
    mt: { xs: '1rem', md: '0' },
    display: {
      md: 'flex',
      xs: 'block'
    },
    '& img': {
      objectFit: 'cover',
      marginLeft: {
        xs: 'none',
        lg: 'auto'
      },
      width: {
        xs: '100%',
        ml: '70%'
      },
      transform: {
        xs: 'translateX(-3.5rem)',
        sm: 'none'
      }
    }
  },
  bkImgReverseLeft: {
    width: {
      xs: `calc(100% + ${spacing(2)})`,
      md: '100%'
    },
    height: '100%',
    maxHeight: '40rem',
    display: {
      md: 'flex',
      xs: 'block'
    },
    mx: {
      xs: `-${spacing(1)}`,
      md: '0'
    },
    mb: { xs: spacing(2), md: '0' },
    '& img': {
      objectFit: 'cover',
      objectPosition: { xs: 'center', lg: 'left' },
      width: {
        xs: '100%',
        ml: '70%'
      },
      maxHeight: '40rem'
    }
  },
  bkImgReverseCenter: {
    width: '100%',
    height: '100%',
    maxHeight: '40rem',
    display: {
      md: 'flex',
      xs: 'block'
    },
    '& img': {
      objectFit: 'cover',
      width: {
        xs: '100%',
        ml: '90%'
      },
      maxHeight: '40rem'
    }
  },
  bkImgNormalCenterAqua: {
    width: {
      xs: `calc(100% + ${spacing(2)})`,
      md: '100%'
    },
    height: '100%',
    maxHeight: '40rem',
    mx: {
      xs: `-${spacing(1)}`,
      md: '0'
    },
    mb: { xs: spacing(2), md: '0' },
    display: {
      md: 'flex',
      xs: 'block'
    },
    '& img': {
      objectFit: 'cover',
      marginLeft: {
        xs: 'none',
        lg: 'auto'
      },
      width: {
        xs: '100%',
        ml: '95%',
        xl: '80%'
      },
      maxHeight: '40rem'
    }
  },
  bkImgReverseCenterAqua: {
    width: {
      xs: `calc(100% + ${spacing(2)})`,
      md: '100%'
    },
    height: '100%',
    maxHeight: '40rem',
    display: {
      md: 'flex',
      xs: 'block'
    },
    mx: {
      xs: `-${spacing(1)}`,
      md: '0'
    },
    mb: { xs: spacing(2), md: '0' },
    '& img': {
      objectFit: 'cover',
      width: {
        xs: '100%',
        ml: '95%',
        xl: '80%'
      },
      maxHeight: '40rem'
    }
  },
  bkImgNormalCenterGreen: {
    width: '100%',
    height: '100%',
    maxHeight: '40rem',
    display: {
      md: 'flex',
      xs: 'block'
    },
    '& img': {
      objectFit: 'cover',
      width: {
        xs: '100%',
        ml: '95%',
        xl: '80%'
      },
      maxHeight: '40rem'
    }
  },
  bkImgalexanderHall: {
    width: '100%',
    height: '100%',
    maxHeight: '40rem',
    display: {
      md: 'flex',
      xs: 'block'
    },
    justifyContent: {
      xs: 'center',
      md: 'flex-end'
    },
    '& img': {
      objectFit: 'cover',
      width: {
        xs: '100%',
        ml: '95%',
        xl: '80%'
      }
    }
  },
  bkImgredGradient: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    paddingTop: {
      xs: '4rem',
      md: '0'
    },
    '& img': {
      objectFit: 'contain',
      width: {
        xs: '90%',
        ss: '70%',
        sm: '50%',
        md: '75%',
        lg: '100%',
        ml: '80%',
        xl: '70%'
      },
      transform: {
        xs: 'scale(1)',
        lg: 'scale(0.8)',
        xl: 'scale(0.5)'
      }
    }
  },

  qrText: {
    color: palette.primary.main,
    mt: '1.5rem',
    mb: '1rem'
  },

  qrImgBox: {
    display: 'flex',
    gap: '1rem',
    my: '1rem'
  },

  centerHeading: {
    color: palette.primary.main,
    py: '0.5rem',
    mb: '1rem'
  },
  qrContainer: {
    width: '100%'
  }
};

export default styles;
